"use client";

import * as React from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/date-picker";
import { format, parseISO } from "date-fns";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { trackSearch } from "@/lib/analytics";

function parseDate(value: string | null): Date | undefined {
  if (!value) return undefined;
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export function AdvancedSearch() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const initialHasFilters = Boolean(
    searchParams.get("startDate") || searchParams.get("endDate") ||
    searchParams.get("minHot") || searchParams.get("maxHot"),
  );

  const [q, setQ] = React.useState(searchParams.get("q") || "");
  const [startDate, setStartDate] = React.useState<Date | undefined>(() => parseDate(searchParams.get("startDate")));
  const [endDate, setEndDate] = React.useState<Date | undefined>(() => parseDate(searchParams.get("endDate")));
  const [minHot, setMinHot] = React.useState(searchParams.get("minHot") || "");
  const [maxHot, setMaxHot] = React.useState(searchParams.get("maxHot") || "");
  const [showAdvanced, setShowAdvanced] = React.useState(initialHasFilters);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setQ(searchParams.get("q") || "");
    setStartDate(parseDate(searchParams.get("startDate")));
    setEndDate(parseDate(searchParams.get("endDate")));
    setMinHot(searchParams.get("minHot") || "");
    setMaxHot(searchParams.get("maxHot") || "");
  }, [searchParams]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const query = q.trim().replace(/\s+/g, " ");
    if (!query) {
      setError("请输入要查找的人物、事件或话题");
      return;
    }
    if (startDate && endDate && startDate > endDate) {
      setError("开始日期不能晚于结束日期");
      setShowAdvanced(true);
      return;
    }
    if (minHot && maxHot && Number(minHot) > Number(maxHot)) {
      setError("最低热度不能高于最高热度");
      setShowAdvanced(true);
      return;
    }

    setError("");
    const params = new URLSearchParams();
    params.set("q", query);
    if (startDate) params.set("startDate", format(startDate, "yyyy-MM-dd"));
    if (endDate) params.set("endDate", format(endDate, "yyyy-MM-dd"));
    if (minHot) params.set("minHot", minHot);
    if (maxHot) params.set("maxHot", maxHot);
    trackSearch(query, "page");
    router.push(`/search?${params.toString()}`);
  };

  const clearFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setMinHot("");
    setMaxHot("");
    setError("");
  };

  const hasFilters = Boolean(startDate || endDate || minHot || maxHot);

  return (
    <form onSubmit={handleSearch} className="space-y-4" role="search">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative min-w-0 flex-1 sm:max-w-2xl">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            name="q"
            type="search"
            aria-label="搜索历史热搜"
            aria-invalid={!!error && !q.trim()}
            placeholder="输入关键词，如：福建晋江"
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              if (error) setError("");
            }}
            className="h-11 pl-9 pr-9"
            maxLength={50}
          />
          {q && (
            <button
              type="button"
              aria-label="清空关键词"
              className="absolute right-2 top-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => setQ("")}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <Button type="submit" className="h-11 flex-1 px-6 sm:flex-none">搜索</Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 flex-1 sm:flex-none"
            aria-expanded={showAdvanced}
            aria-controls="advanced-search-filters"
            onClick={() => setShowAdvanced((value) => !value)}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            {showAdvanced ? "收起筛选" : hasFilters ? "已筛选" : "筛选"}
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

      {showAdvanced && (
        <div id="advanced-search-filters" className="space-y-4 rounded-xl border bg-muted/30 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-medium">缩小搜索范围</h2>
              <p className="mt-1 text-xs text-muted-foreground">日期按词条出现时间筛选，热度按历史最高值筛选</p>
            </div>
            {hasFilters && (
              <Button type="button" variant="ghost" size="sm" onClick={clearFilters} className="shrink-0 text-muted-foreground">
                <X className="mr-1 h-4 w-4" />清除
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label>开始日期</Label>
              <div className="[&_button]:w-full"><DatePicker date={startDate} onDateChange={setStartDate} placeholder="不限开始日期" /></div>
            </div>
            <div className="space-y-2">
              <Label>结束日期</Label>
              <div className="[&_button]:w-full"><DatePicker date={endDate} onDateChange={setEndDate} placeholder="不限结束日期" /></div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="search-min-hot">最低热度</Label>
              <Input id="search-min-hot" type="number" min="0" inputMode="numeric" placeholder="不限" value={minHot} onChange={(event) => setMinHot(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="search-max-hot">最高热度</Label>
              <Input id="search-max-hot" type="number" min="0" inputMode="numeric" placeholder="不限" value={maxHot} onChange={(event) => setMaxHot(event.target.value)} />
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
